import { APP_NAME } from '@foosball/shared';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';

import { ApiError } from '../api/client';
import { Button, Card, Field, FormError } from '../components/ui';
import { useAuth } from '../lib/auth-context';

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Where the user was heading before being bounced to /login.
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  if (user) {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await login(email, password);
      void navigate(from, { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not sign in. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-10">
      <h1 className="mb-1 text-3xl font-bold tracking-tight">Sign in</h1>
      <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">{APP_NAME}</p>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <FormError message={error} />

          <Field
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Field
            label="Password"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Button type="submit" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>

          <p className="text-center text-sm">
            <Link
              to="/forgot-password"
              className="text-slate-500 hover:underline dark:text-slate-400"
            >
              Forgotten your password?
            </Link>
          </p>
        </form>
      </Card>

      <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
        No account?{' '}
        <Link to="/register" className="font-medium text-emerald-700 hover:underline dark:text-emerald-400">
          Create one
        </Link>
      </p>

      <p className="mt-3 text-center text-xs text-slate-400 dark:text-slate-500">
        A personal project, not run or controlled by OXTS.{' '}
        <Link to="/privacy" className="hover:underline">
          Privacy notice
        </Link>
      </p>
    </main>
  );
}
